import { DataVersion, IncidentProduct } from '@hakimo-ui/hakimo/types';
import { ImageUpload } from '@hakimo-ui/hakimo/ui-elements';
import { toast } from '@hakimo-ui/hakimo/util';
import { Button, Modal } from '@hakimo-ui/shared/ui-base';
import { useEffect, useState } from 'react';
import { useReportLogoActions } from './useReportLogoActions';

interface Props {
  open: boolean;
  onClose: () => void;
  alarmId: number | string;
  product: IncidentProduct;
  dataVersion?: DataVersion;
  currentLogoUrl: string | null;
  onLogoChanged: () => void;
}

const ACCEPTED = 'image/png,image/jpeg';
const MAX_BYTES = 2 * 1024 * 1024;

export function ReportLogoModal({
  open,
  onClose,
  alarmId,
  product,
  dataVersion,
  currentLogoUrl,
  onLogoChanged,
}: Props) {
  const [file, setFile] = useState<File | null>(null);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [cleared, setCleared] = useState(false);

  const {
    uploadLogo,
    removeLogo,
    isUploading: isLoading,
    isRemoving,
  } = useReportLogoActions({
    product,
    alarmId,
    dataVersion,
    onDone: () => {
      onLogoChanged();
      onClose();
    },
  });

  useEffect(() => {
    if (open) {
      setFile(null);
      setObjectUrl(null);
      setCleared(false);
    }
  }, [open]);

  useEffect(() => {
    if (!objectUrl) {
      return;
    }
    return () => URL.revokeObjectURL(objectUrl);
  }, [objectUrl]);

  const onImageChange = (next: File) => {
    if (next.size > MAX_BYTES) {
      toast('Logo must be 2 MB or smaller', { type: 'error' });
      return;
    }
    setFile(next);
    setObjectUrl(URL.createObjectURL(next));
    setCleared(false);
  };

  const onImageRemove = () => {
    setFile(null);
    setObjectUrl(null);
    setCleared(true);
  };

  const preview = objectUrl ?? (cleared ? null : currentLogoUrl);
  const pendingRemoval = cleared && !file && !!currentLogoUrl;

  const onSubmit = () => {
    if (file) {
      uploadLogo(file);
      return;
    }
    if (pendingRemoval) {
      removeLogo();
    }
  };

  const actions = (
    <>
      <Button
        variant="primary"
        onClick={onSubmit}
        disabled={(!file && !pendingRemoval) || isLoading || isRemoving}
        loading={isLoading || isRemoving}
      >
        Submit
      </Button>
      <Button
        variant="outline"
        onClick={onClose}
        disabled={isLoading || isRemoving}
      >
        Cancel
      </Button>
    </>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      closable
      title="Report Logo"
      footer={actions}
    >
      <div className="w-[28rem] max-w-full px-8 py-5">
        <ImageUpload
          imagePreview={preview}
          onImageChange={onImageChange}
          onImageRemove={onImageRemove}
          accept={ACCEPTED}
          label="This will be shown in the report header"
          placeholder="Upload a logo"
          helperText="PNG or JPEG, up to 2 MB"
        />
        {preview && !file && (
          <p className="text-onlight-text-3 dark:text-ondark-text-2 mt-3 text-xs">
            This logo is already saved for this incident. Upload another to
            replace it, or remove it to drop it from the report.
          </p>
        )}
      </div>
    </Modal>
  );
}

export default ReportLogoModal;
